import { useState } from "react";
import CSVreader from "react-csv-reader";
import { json2csv } from "json-2-csv";
import dayjs from "dayjs";

import {
  Box,
  Typography,
  Grid,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Button,
  StyledEngineProvider,
  ThemeProvider
} from "@mui/material";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";

import { Select, DefaultPaper } from "@aclymatepackages/atoms";
import { formatDollars } from "@aclymatepackages/formatters";
import { editRowData } from "@aclymatepackages/array-immutability-helpers";
import { mergeMainTheme, mergeDarkTheme } from "@aclymatepackages/themes";
import { YesNoQuestion } from "@aclymatepackages/modules";

import { isObjectEmpty } from "../../helpers/otherHelpers";
import { uploadFileToStorage, useAuth } from "../../helpers/firebase";
import { getAccountId } from "../../helpers/accountHelpers";
import usePlatformLayoutContext from "../../helpers/contexts/platformLayout";
import { useSharedFormLoading } from "../../helpers/components/inputs";

const parseCsv = (data, knownFields) => {
  const validKnownFields = knownFields.filter(
    (fieldRow) => fieldRow.field !== "metadata" || fieldRow.metadataLabel
  );
  const knownFieldNames = validKnownFields.map((row) => row.name.toLowerCase());

  const matchKnownFields = (array) =>
    knownFieldNames.every((arrayEl) =>
      array.map((arrayEl) => arrayEl.toLowerCase()).includes(arrayEl)
    );

  const headerRowIdx = data.findIndex((row) => matchKnownFields(row));
  if (headerRowIdx < 0) {
    return null;
  }

  const headerRow = data[headerRowIdx];

  const lowercasedHeaderRow = headerRow.map((column) => column.toLowerCase());

  const { headerColumns } = validKnownFields.reduce(
    ({ headerColumns: acc, consumedIndices }, fieldRow) => {
      const targetName = fieldRow.name.toLowerCase();
      const headerIndex = lowercasedHeaderRow.findIndex(
        (column, idx) => !consumedIndices.has(idx) && column === targetName
      );
      const fieldName =
        fieldRow.field === "metadata" ? fieldRow.metadataLabel : fieldRow.field;
      const nextConsumed = new Set(consumedIndices);
      if (headerIndex !== -1) {
        nextConsumed.add(headerIndex);
      }
      return {
        headerColumns: [...acc, { headerIndex, fieldName }],
        consumedIndices: nextConsumed
      };
    },
    { headerColumns: [], consumedIndices: new Set() }
  );

  const indexToFieldName = headerColumns.reduce(
    (acc, { headerIndex, fieldName }) =>
      headerIndex === -1 ? acc : { ...acc, [headerIndex]: fieldName },
    {}
  );

  const dataArray = data.slice(headerRowIdx + 1, data.length);

  return dataArray.map((row) => {
    const headerEntries = row
      .map((rowEl, rowIdx) => {
        const fieldName = indexToFieldName[rowIdx];
        if (!fieldName) {
          return null;
        }
        const value =
          fieldName.toLowerCase() === "mileage" ? Number(rowEl) : rowEl;

        return [fieldName, value];
      })
      .filter(Boolean);

    return Object.fromEntries(headerEntries);
  });
};

const allCellsFilled = (row) =>
  row.reduce(
    (allCellsFilled, currentCell) => allCellsFilled && !!currentCell,
    true
  );

const groupMetadataFields = (rows, metadataLabels) => {
  if (!metadataLabels.length) {
    return rows;
  }
  return rows.map((row) => {
    const metadata = Object.fromEntries(
      metadataLabels
        .filter((label) => label in row)
        .map((label) => [label, row[label]])
    );
    const baseRow = Object.fromEntries(
      Object.entries(row).filter(([key]) => !metadataLabels.includes(key))
    );
    return Object.keys(metadata).length ? { ...baseRow, metadata } : baseRow;
  });
};

const useOnProcessData = ({
  hasUserConfirmedUpload,
  additionalMetadata = {}
}) => {
  const [user] = useAuth();

  return async ({
    fileInfo,
    docType,
    data,
    matchedHeaders,
    originalFileObj,
    processData
  }) => {
    const { name: fileName } = fileInfo;
    const { uid } = user;

    const metadataLabels = matchedHeaders
      .filter((header) => header.field === "metadata" && header.metadataLabel)
      .map((header) => header.metadataLabel);

    const formattedCsvData = parseCsv(data, matchedHeaders);

    if (processData) {
      return await processData({
        data: groupMetadataFields(formattedCsvData, metadataLabels),
        fileInfo,
        docType,
        matchedHeaders,
        originalFileObj,
        hasUserConfirmedUpload
      });
    }

    const id = getAccountId();
    const storagePath = `${id}/csv/${docType}/${dayjs().format(
      "MM-DD-YYYY--"
    )}${fileName.split(" ").join("_")}`;

    const csv = json2csv(formattedCsvData);
    const csvBlob = new Blob([csv], { type: "text/csv" });

    const uploadResult = await uploadFileToStorage({
      path: `${storagePath}/raw-data.csv`,
      fileObject: originalFileObj,
      hasUserConfirmedUpload
    });
    const { isDuplicateFileDetected: isDuplicateFileDetectedFromUpload } =
      uploadResult || {};

    if (isDuplicateFileDetectedFromUpload && !hasUserConfirmedUpload) {
      return { isDuplicateFileDetected: true };
    }

    const matchedHeadersMetadataObj = matchedHeaders.reduce(
      (currentObj, { field, name }) => ({
        ...currentObj,
        [field]: name
      }),
      {}
    );

    return await uploadFileToStorage({
      path: `${storagePath}/processed-data.csv`,
      fileObject: csvBlob,
      fileMetadata: {
        customMetadata: {
          fileName,
          dateLoaded: new Date(),
          userId: uid,
          ...matchedHeadersMetadataObj,
          ...additionalMetadata,
          ...(metadataLabels.length && {
            metadataFields: JSON.stringify(metadataLabels)
          })
        }
      },
      hasUserConfirmedUpload
    });
  };
};

const FileSelectBlock = ({
  setCsvData,
  setHeaderIdx,
  fileInfo,
  setFileInfo,
  knownFields,
  isCsvUploaded,
  setOriginalFileObj,
  requiredFields
}) => {
  const { name: fileName } = fileInfo;

  const { isSimulation } = usePlatformLayoutContext();

  const findHeaderRowIdx = (data) => {
    if (!Array.isArray(data)) {
      return null;
    }

    const knownHeaders = knownFields.map(({ header }) => header.toLowerCase());
    const knownHeaderMatches = data.map(
      (row) =>
        row.filter((cell) => knownHeaders.includes(cell.toLowerCase())).length
    );

    const matchedHeaderRows = knownHeaderMatches.filter((count) => count);

    if (!matchedHeaderRows.length) {
      const headerRowIdx = data.findIndex((row) => allCellsFilled(row));
      return headerRowIdx === -1 ? 0 : headerRowIdx;
    }

    const { idx: maxMatchedHeadersIdx } = knownHeaderMatches.reduce(
      ({ matches, idx: matchedIdx }, curr, idx) =>
        Number(curr) > Number(matches)
          ? { matches: curr, idx }
          : { matches, idx: matchedIdx },
      { matches: 0, idx: 0 }
    );

    if (maxMatchedHeadersIdx > 50) {
      return 0;
    }

    return maxMatchedHeadersIdx;
  };

  const onCsvUpload = async (data, fileInfo, originalFile) => {
    const headerIdx = findHeaderRowIdx(data);

    setFileInfo(fileInfo);
    setCsvData(data);
    setOriginalFileObj(originalFile);
    return setHeaderIdx(headerIdx);
  };

  return (
    <>
      <Box p={2}>
        <Grid container alignItems="center" direction="column" spacing={2}>
          {requiredFields && (
            <Grid item>
              <ThemeProvider theme={mergeMainTheme}>
                <DefaultPaper>
                  <Typography variant="h4">
                    There's no need for a template
                  </Typography>
                  <Typography
                    variant="sutbtitle1"
                    color="textSecondary"
                    paragraph
                  >
                    Our CSV loader is designed to handle your data in any format
                    as long as you can identify the following columns:
                  </Typography>
                  <Grid container direction="column" spacing={1}>
                    {requiredFields.map(({ label, description, icon }, idx) => (
                      <Grid
                        item
                        key={`required-field-${idx}`}
                        container
                        spacing={2}
                        alignItems="center"
                        wrap="nowrap"
                      >
                        <Grid
                          item
                          xs={1}
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end"
                          }}
                        >
                          <FontAwesomeIcon icon={icon} size="xl" />
                        </Grid>
                        <Grid item>
                          <Typography variant="h6" display="inline">
                            {label}-{" "}
                          </Typography>
                          <Typography
                            variant="body1"
                            color="textSecondary"
                            display="inline"
                          >
                            {description}
                          </Typography>
                        </Grid>
                      </Grid>
                    ))}
                  </Grid>
                </DefaultPaper>
              </ThemeProvider>
            </Grid>
          )}
          <Grid item>
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={mergeMainTheme}>
                <Button
                  variant="contained"
                  color="secondary"
                  id="csv-upload-btn"
                  disabled={isCsvUploaded || isSimulation}
                >
                  <label htmlFor="file-upload-input">Upload a file</label>
                </Button>
              </ThemeProvider>
            </StyledEngineProvider>
            <CSVreader
              onFileLoaded={(data, fileInfo, originalFile) =>
                onCsvUpload(data, fileInfo, originalFile)
              }
              inputId="file-upload-input"
              inputStyle={{ display: "none" }}
            />
          </Grid>
          {fileName && (
            <Grid item>
              <Typography variant="body2" align="center">
                {fileName}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Box>
    </>
  );
};

const HeadCell = ({
  header,
  idx,
  allFieldOptions,
  onFieldSelect,
  editHeaders
}) => {
  const { name, field, metadataLabel } = header;

  return (
    <TableCell>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          height: "100%",
          gap: 0.5,
          py: 1
        }}
      >
        <Select
          options={allFieldOptions}
          value={field}
          editValue={onFieldSelect(idx)}
          size="small"
          id={`head-column-select-${idx}`}
        />
        {field === "metadata" && (
          <TextField
            size="small"
            placeholder="Field name"
            value={metadataLabel || ""}
            onChange={(e) =>
              editRowData(idx, editHeaders)("metadataLabel", e.target.value)
            }
          />
        )}
        {name}
      </Box>
    </TableCell>
  );
};

const CsvTableHead = ({ headers, editHeaders, fieldOptions }) => {
  const allFieldOptions = [
    { label: "None", value: "none" },
    ...fieldOptions,
    { label: "Metadata", value: "metadata" }
  ];

  const onFieldSelect = (rowIdx) => (field) => {
    editRowData(rowIdx, editHeaders)("field", field);
    if (field === "metadata" && !headers[rowIdx].metadataLabel) {
      editRowData(rowIdx, editHeaders)("metadataLabel", headers[rowIdx].name);
    }
    const existingFieldColumnIdx = headers.findIndex(
      (header) => header.field === field && header.field !== "metadata"
    );
    if (existingFieldColumnIdx !== -1) {
      return editRowData(existingFieldColumnIdx, editHeaders)("field", "none");
    }
    return;
  };

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={mergeMainTheme}>
        <TableHead style={{ backgroundColor: "white" }}>
          <TableRow
            style={{ verticalAlign: "top" }}
            id="csv-table-head"
            verticalAlign="bottom"
          >
            {headers.map((header, idx) => (
              <HeadCell
                key={`header-row-cell-${idx}`}
                header={header}
                idx={idx}
                allFieldOptions={allFieldOptions}
                onFieldSelect={onFieldSelect}
                editHeaders={editHeaders}
              />
            ))}
          </TableRow>
        </TableHead>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

const CsvTable = ({
  data,
  headerIdx,
  setHeaderIdx,
  headers,
  editHeaders,
  fieldOptions,
  docType
}) => {
  const maxRowWidth = data.reduce(
    (maxWidth, currentRow) =>
      maxWidth < currentRow.length ? currentRow.length : maxWidth,
    0
  );

  const boldStyleTernary = (rowIdx) =>
    !headers && headerIdx === rowIdx ? { fontWeight: "700" } : {};

  const fixFormatDollars = (cell, docType, row) => {
    if (!data || Array.isArray(data) === false) {
      return cell;
    }

    const newHeaderRowIdx = data.findIndex(({ id }) => id === headerIdx);

    const cardHeaderRow =
      headers?.map((header) => header.name) ||
      data[newHeaderRowIdx < 0 ? 0 : newHeaderRowIdx].data;

    const cardHeaderIdx = cardHeaderRow.findIndex(
      (header) => header.toLowerCase() === "card"
    );

    const isColumnSameAsCard =
      row.findIndex((rowItem) => rowItem === cell) === cardHeaderIdx;

    if (!!Number(cell) && docType === "transactions" && !isColumnSameAsCard) {
      return formatDollars(cell);
    }
    return cell;
  };

  const CsvTableRow = ({ row, rowIdx }) => {
    const filledCellsSum = row.reduce(
      (sum, cell) => (!!cell ? sum + 1 : sum),
      0
    );

    const colSpan = allCellsFilled(row)
      ? 1
      : Math.floor(row.length / filledCellsSum);

    const colSpanSum = colSpan * filledCellsSum;

    const isSelectedHeaderRow = headerIdx === rowIdx;

    return (
      <StyledEngineProvider injectFirst>
        <ThemeProvider
          theme={isSelectedHeaderRow ? mergeMainTheme : mergeDarkTheme}
        >
          <TableRow
            hover={!headers}
            selected={!headers && headerIdx === rowIdx}
            colSpan={maxRowWidth}
            onClick={!headers ? () => setHeaderIdx(rowIdx) : () => {}}
            style={{
              backgroundColor:
                !headers && isSelectedHeaderRow ? "white" : "inherit"
            }}
            id={`csv-row-${rowIdx}`}
          >
            {!headers && (
              <>
                <TableCell>
                  {isSelectedHeaderRow ? (
                    <FontAwesomeIcon icon={faArrowRight} size="lg" />
                  ) : (
                    ""
                  )}
                </TableCell>
                <TableCell style={boldStyleTernary(rowIdx)}>{rowIdx}</TableCell>
              </>
            )}
            {row.map(
              (cell, idx) =>
                (!!cell || rowIdx > headerIdx) && (
                  <TableCell
                    key={`csv-row-cell-${idx}`}
                    style={boldStyleTernary(rowIdx)}
                    colSpan={colSpan}
                  >
                    {fixFormatDollars(cell, docType, row)}
                  </TableCell>
                )
            )}
            {colSpan > 1 && colSpanSum < row.length && (
              <TableCell colSpan={row.length - colSpanSum}></TableCell>
            )}
          </TableRow>
        </ThemeProvider>
      </StyledEngineProvider>
    );
  };

  return (
    <Table size="small">
      {headers && (
        <CsvTableHead
          headers={headers}
          editHeaders={editHeaders}
          fieldOptions={fieldOptions}
        />
      )}
      <TableBody>
        {data.map(({ data, id }) => (
          <CsvTableRow row={data} rowIdx={id} key={`csv-data-row-${id}`} />
        ))}
      </TableBody>
    </Table>
  );
};

const IdHeaderRow = ({ data, headerIdx, setHeaderIdx, docType }) => {
  const tableData = data
    .map((row, idx) => ({ data: row, id: idx }))
    .filter(({ id }) => id > headerIdx - 4 && id < headerIdx + 4);

  return (
    <>
      <Grid
        container
        spacing={2}
        direction="column"
        justifyContent="center"
        alignItems="center"
      >
        <Grid
          item
          style={{ overflowX: "auto", overflowY: "hidden", width: "100%" }}
        >
          <CsvTable
            data={tableData}
            headerIdx={headerIdx}
            setHeaderIdx={setHeaderIdx}
            docType={docType}
          />
        </Grid>
        <Grid item>
          <Typography>
            {`Confirm that row ${headerIdx} contains the headers for your table?`}
          </Typography>
        </Grid>
      </Grid>
    </>
  );
};

const buildIdHeaderRow = ({
  setMatchedHeaders,
  knownFields,
  csvData,
  headerIdx,
  setHeaderIdx,
  setColumnLength,
  docType
}) => {
  const processHeadersRow = (headers) => {
    const truncatedHeaders = headers.filter((header) => header);

    const findKnownFieldForHeader = (csvHeader) => {
      const exactMatch = knownFields.find(
        ({ header: fieldHeader }) => fieldHeader.toLowerCase() === csvHeader
      );
      if (exactMatch) {
        return exactMatch.field;
      }
      const substringMatch = knownFields.find(({ header: fieldHeader }) => {
        const known = fieldHeader.toLowerCase();
        return known.includes(csvHeader) || csvHeader.includes(known);
      });
      return substringMatch?.field;
    };

    const knownHeaders = truncatedHeaders.map((header) => {
      const csvHeader = header.toLowerCase();
      const knownField = findKnownFieldForHeader(csvHeader);
      return { name: header, field: knownField || "none" };
    });

    setColumnLength(truncatedHeaders.length);
    return setMatchedHeaders(
      knownHeaders.map((header, idx, self) => {
        const isDuplicateMatch =
          self.findIndex((selfHeader) => selfHeader.field === header.field) !==
          idx;
        if (isDuplicateMatch) {
          return { ...header, field: "none" };
        }
        return header;
      })
    );
  };

  return {
    label: "Confirm Header Rows",
    size: "lg",
    title:
      "We need you to confirm the row in your CSV that contains the headers for your columns",
    input: (
      <IdHeaderRow
        data={csvData}
        headerIdx={headerIdx}
        setHeaderIdx={setHeaderIdx}
        docType={docType}
      />
    ),
    buttonText: "confirm",
    onAdvanceForm: () => processHeadersRow(csvData[headerIdx])
  };
};

const useMatchColumnHeaders = ({
  data,
  headerIdx,
  matchedHeaders,
  setMatchedHeaders,
  fileInfo,
  docType,
  fieldValidator,
  processData,
  fieldOptions,
  columnLength,
  originalFileObj,
  setOpen,
  successMessage = "We're processing your CSV. Your data will show up in your dashboard once we're done.",
  submitText,
  additionalMetadata
}) => {
  const [isDuplicateFileDetected, setIsDuplicateFileDetected] = useState(false);
  const [hasUserConfirmedUpload, setHasUserConfirmedUpload] = useState(false);

  const { activateSnackbar } = usePlatformLayoutContext();
  const { setFormLoading } = useSharedFormLoading();
  const onProcessData = useOnProcessData({
    hasUserConfirmedUpload,
    additionalMetadata
  });

  const tableData = data
    .map((row, idx) => {
      const rowWithoutEmptyColumns = row.filter((_, idx) => idx < columnLength);

      return { data: rowWithoutEmptyColumns, id: idx };
    })
    .filter((_, idx) => idx > headerIdx && idx < headerIdx + 3);

  const onNextStep = async () => {
    const { success } = fieldValidator(matchedHeaders);

    if (!success) {
      return;
    }

    const filteredMatchedHeaders = matchedHeaders.filter(
      (header) =>
        header.field !== "none" &&
        (header.field !== "metadata" || header.metadataLabel)
    );
    setMatchedHeaders(filteredMatchedHeaders);

    setFormLoading(true);

    const onProcessDataResult = await onProcessData({
      processData,
      data,
      matchedHeaders: filteredMatchedHeaders,
      fileInfo,
      docType,
      originalFileObj
    });
    const { isDuplicateFileDetected: isDuplicateFileDetectedFromProcessData } =
      onProcessDataResult || {};

    if (isDuplicateFileDetectedFromProcessData && !hasUserConfirmedUpload) {
      setFormLoading(false);
      return setIsDuplicateFileDetected(true);
    }

    activateSnackbar({
      message: successMessage,
      alert: "success"
    });
    setFormLoading(false);
    return setOpen(false);
  };

  const { message: errorMessage } = fieldValidator(matchedHeaders);

  const duplicateFileDetectedInputRow = isDuplicateFileDetected
    ? [
        {
          size: "lg",
          title: "Duplicate File Detected",
          input: (
            <YesNoQuestion
              question={
                "We've detected that this file has already been uploaded. Would you still like to upload it?"
              }
              value={hasUserConfirmedUpload}
              setValue={(value) => setHasUserConfirmedUpload(value)}
            />
          ),
          label: "Duplicate File Detected",
          onAdvanceForm: onNextStep,
          buttonText: "Next",
          buttonDisabled: !hasUserConfirmedUpload
        }
      ]
    : [];

  return [
    {
      size: "lg",
      title:
        'Please match your headers to the columns that we need to be able to read your data. Please choose "None" for any column that you want us to ignore.',
      input: (
        <Box
          p={2}
          style={{ overflowX: "auto", overflowY: "hidden", width: "100%" }}
        >
          <Grid container spacing={2} direction="column" alignItems="center">
            <Grid item>
              <CsvTable
                data={tableData}
                headers={matchedHeaders}
                headerIdx={headerIdx}
                editHeaders={setMatchedHeaders}
                fieldOptions={fieldOptions}
                docType={docType}
              />
            </Grid>
            {isDuplicateFileDetected && !hasUserConfirmedUpload && (
              <Grid item>
                <Typography>
                  {`We've detected that this file has already been uploaded. Would you like to use the existing data?`}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      ),
      label: "Match Header Columns",
      onAdvanceForm: onNextStep,
      formAdvanceErrorMessage: errorMessage,
      buttonText: submitText || "Next"
    },
    ...duplicateFileDetectedInputRow
  ];
};

const useCsvUploader = ({
  fileInfo: parentFileInfo,
  setFileInfo: parentSetFileInfo,
  processData,
  fieldOptions,
  knownFields,
  fieldValidator,
  docType,
  title,
  firstStepText,
  matchedHeaders: parentMatchedHeaders,
  setMatchedHeaders: setParentMatchedHeaders,
  requiredFields = [],
  submitText,
  setOpen,
  successMessage,
  additionalMetadata
}) => {
  const [matchedHeaders, setMatchedHeaders] = useState([]);
  const [csvData, setCsvData] = useState([[]]);

  const [headerIdx, setHeaderIdx] = useState(0);
  const [fileInfo, setFileInfo] = useState({});
  const [columnLength, setColumnLength] = useState(0);
  const [isCsvUploaded, setIsCsvUploaded] = useState(false);
  const [originalFileObj, setOriginalFileObj] = useState(null);

  const usableFileInfo = parentFileInfo || fileInfo;
  const usableSetFileInfo = parentSetFileInfo || setFileInfo;
  const usableMatchedHeaders = parentMatchedHeaders || matchedHeaders;
  const usableSetMatchedHeaders = setParentMatchedHeaders || setMatchedHeaders;

  const filterEmptyRows = (csvDataArr) =>
    csvDataArr.filter((row) => row.some((cell) => cell !== ""));

  const idHeaderRow = buildIdHeaderRow({
    setMatchedHeaders: usableSetMatchedHeaders,
    knownFields,
    csvData: filterEmptyRows(csvData),
    headerIdx,
    setHeaderIdx,
    setColumnLength,
    docType
  });

  const matchColumnHeaders = useMatchColumnHeaders({
    data: filterEmptyRows(csvData),
    requiredFields,
    headerIdx,
    matchedHeaders: usableMatchedHeaders,
    setMatchedHeaders: usableSetMatchedHeaders,
    fileInfo: usableFileInfo,
    docType,
    fieldValidator,
    processData,
    fieldOptions,
    columnLength,
    originalFileObj,
    setOpen,
    submitText,
    successMessage,
    additionalMetadata
  });

  return [
    {
      label: "Select a file",
      title: title || `Upload your ${docType} CSV`,
      subtitle: `${firstStepText} `,
      input: (
        <FileSelectBlock
          setCsvData={setCsvData}
          setHeaderIdx={setHeaderIdx}
          fileInfo={usableFileInfo}
          setFileInfo={usableSetFileInfo}
          knownFields={knownFields}
          isCsvUploaded={isCsvUploaded}
          setOriginalFileObj={setOriginalFileObj}
          requiredFields={requiredFields}
        />
      ),
      buttonDisabled: isObjectEmpty(usableFileInfo),
      onAdvanceForm: () => setIsCsvUploaded(true)
    },
    idHeaderRow,
    ...matchColumnHeaders
  ];
};
export default useCsvUploader;
